import { CheckCircleOutlined, EyeInvisibleOutlined, EyeTwoTone, HourglassOutlined, QuestionCircleOutlined, StopOutlined } from "@ant-design/icons";
import { Checkbox, Collapse, Form, Input, Radio, Space, Table, Tag, Typography } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { ReactElement } from "react";
import { TextService } from "../../Services/TextService";
import { EventType } from "../../Types/EventType";
import { AnswerOptions, isOnlyComing } from "../../Types/AnswerOptions";

export enum InputType {
    email = "email",
    password = "password",
    multiline = "multiline"
}

export const ReplyTimeForm = (props: any) => {
    const GetText = TextService.GetText;

    const getAnswer = (answerType: any): ReactElement => {
        var foundResult = <CheckCircleOutlined />;
        switch (answerType) {
            case 0:
                foundResult = <span className='accepted'><CheckCircleOutlined /> {GetText("ReplyTimeForm_Accepted")} </span>
                break;
            case 1:
                foundResult = <span className='tentative'><QuestionCircleOutlined /> {GetText("ReplyTimeForm_Tentative")}</span>
                break;
            case 2:
                foundResult = <span className='rejected'><StopOutlined /> {GetText("ReplyTimeForm_Rejected")}</span>
                break;
            case 3:
                foundResult = <span className='tentative'><HourglassOutlined /> {GetText("ReplyTimeForm_Waitlisted")}</span>
                break;
        }

        return foundResult
    };

    const { Panel } = Collapse;

    const locale = {
        emptyText: GetText("ReplyTimeForm_NoAnswers")
    }


    const updateAnswerByTimeId = (timeId: string, answer: string | undefined) => {
        props.onChangeAnswerComplete(timeId, answer);
    }

    const collapseShowMore = (value: string) => (
        <span style={{ color: props.event.primaryColorCode ? props.event.primaryColorCode : "var(--color-primary)" }}>{value}</span>
    );

    // A sign-up has "going" and "not going", like Facebook, or only "going". The other answer options belong to a poll.
    const isSignUp = props.event.eventType !== EventType.Poll;
    const onlyComing = isOnlyComing(props.event);
    const answerOptions = isSignUp ? (onlyComing ? AnswerOptions.Yes : AnswerOptions.YesNo) : props.event.answerOptions;

    // The test id is on the label text, so a click on it selects the answer. Named by the answer value (0 yes, 1 maybe, 2 no, 3 waitlist).
    const answerLabel = (value: string, text: string) => <span data-testid={"answer-" + value}>{text}</span>;

    // A full time offers the waitlist instead of "yes", when the waitlist takes new guests.
    const yesOrWaitlistButton = !props.isFullyBooked
        ? <Radio.Button value="0">{answerLabel("0", GetText(isSignUp ? "Event_SignMeUp" : "ReplyTimeForm_YesButtonText"))}</Radio.Button>
        : props.isWaitlistOpen
            ? <Radio.Button value="3" style={{ width: "auto", whiteSpace: "nowrap" }}>{answerLabel("3", GetText(props.isOnWaitlist ? "Event_StayOnWaitlist" : "Event_JoinWaitlist"))}</Radio.Button>
            : null;

    // With only "Kommer" each time is a checkbox, so it is plain that a time can be left out or taken back.
    // Checked is a place, or the waitlist when the time is full.
    const comingValue = !props.isFullyBooked ? "0" : props.isWaitlistOpen ? "3" : undefined;
    const comingCheckbox = comingValue != null
        ? <Checkbox className='event-checkbox-answer' checked={props.answer === comingValue}
            onChange={e => updateAnswerByTimeId(props.time.id, e.target.checked ? comingValue : undefined)}>
            {answerLabel(comingValue, GetText(comingValue === "0" ? "Event_SignMeUp" : props.isOnWaitlist ? "Event_StayOnWaitlist" : "Event_JoinWaitlist"))}
        </Checkbox>
        : null;

    return (<div data-testid={"time-answers-" + props.time.id}>
        {/* A red tag, like on the member site, so a full time is seen at once. With one time (hideOwnStatus)
            the page already tells it at the top, so it isn't repeated here. */}
        {props.isFullyBooked && !props.hideOwnStatus ? <Tag data-testid="time-fully-booked" color="red" style={{ marginBottom: 8 }}>
            {GetText(props.isWaitlistOpen && !props.isOnWaitlist ? "PublicSiteEvents_FullyBookedWaitlist" : "Event_TimeFullyBooked")}
        </Tag> : null}
        {/* hideOwnStatus: with one time the page already tells at the top that the guest is on the waitlist. */}
        {props.isOnWaitlist && !props.hideOwnStatus ? <Typography><b>{GetText("Event_OnWaitlist")}</b></Typography> : null}
        {/* hideAnswers: a sign-up with only "Kommer" and one time is answered with the "Anmäl mig" button alone. */}
        {!props.hideAnswers ? <Space style={{ width: '100%', justifyContent: 'left' }}>
            {answerOptions === 0 ?
                <Radio.Group value={props.answer} onChange={e => updateAnswerByTimeId(props.time.id, e.target.value)} className='event-radio-answers' size="large" buttonStyle="solid">
                    {yesOrWaitlistButton}
                    <Radio.Button value="1">{answerLabel("1", GetText("ReplyTimeForm_MaybeButtonText"))}</Radio.Button>
                    <Radio.Button value="2">{answerLabel("2", GetText("ReplyTimeForm_NoButtonText"))}</Radio.Button>
                </Radio.Group>
                : ""}

            {answerOptions === 1 ?
                <Radio.Group value={props.answer} onChange={e => updateAnswerByTimeId(props.time.id, e.target.value)} defaultValue="a" className={isSignUp ? 'event-radio-answers' : undefined} size="large" buttonStyle="solid">
                    {yesOrWaitlistButton}
                    <Radio.Button value="2">{answerLabel("2", GetText(isSignUp ? "Event_NotComing" : "ReplyTimeForm_NoButtonText"))}</Radio.Button>
                </Radio.Group>
                : ""}

            {onlyComing ? comingCheckbox : null}

            {answerOptions === 2 && !onlyComing && yesOrWaitlistButton ?
                <Radio.Group value={props.answer} onChange={e => updateAnswerByTimeId(props.time.id, e.target.value)} defaultValue="a" size="large" buttonStyle="solid">
                    {yesOrWaitlistButton}
                </Radio.Group>
                : ""}
        </Space> : null}
        <div className='participant-details'>
            {props.time.participants != null ? <Space style={{ width: '100%', justifyContent: 'left' }}>
                {props.time.acceptedCount} {GetText("ReplyTimeForm_GuestsAnsweredYes")}
            </Space> : null}
            {props.time.participants != null ? <Collapse defaultActiveKey={['1']} ghost>
                <Panel style={{ textAlign: "left" }} header={collapseShowMore(GetText("ReplyTimeForm_ShowOtherGuestReplyText"))} key="0">
                    <Table className='event-showparticipants-table' locale={locale} dataSource={props.time.participants}
                        columns={[{
                            title: GetText("ReplyTimeForm_OtherAnswersNameColumn"), dataIndex: 'name', key: 'name',
                        }, { title: GetText("ReplyTimeForm_OtherAnswersEmailColumn"), dataIndex: 'email', key: 'email' }, {
                            title: GetText("ReplyTimeForm_OtherAnswersAnswerColumn"), dataIndex: 'answer', key: 'answer', render: (_: any, participant: any) => (
                                <span><b>{getAnswer(participant.answer)}</b><br />
                                </span>
                            ),
                        }]}
                    >
                    </Table>
                </Panel>
            </Collapse> : null}
        </div>
    </div>
    );
};
